import { useContext, useEffect, useState } from 'react'
import { CreateNoteInput, NoteTypesQuery, useCreateNoteMutation, useNoteTypesLazyQuery } from '../../../../../generated'
import {
  CANCEL,
  CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE,
  Note_CREATED_SUCCESS,
  SOMETHING_WENT_WRONG
} from '../../../../constants'
import { Alert } from '../../../common/Alert'
import { yupResolver } from '@hookform/resolvers/yup'
import { addCaseNoteValidationSchema } from '../../../../validationSchema'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { CustomModal } from '../../../common/CustomModal'
import { CustomController } from '../../../common/CustomController'
import { Button, CircularProgress, DialogActions } from '@mui/material'
import { AuthContext } from '../../../../context'

interface CreateBillingNoteModalProps {
  isOpen: boolean
  caseId: string
  handleClose: () => void
  refetch: () => void
}

const CreateBillingNoteModal = ({ isOpen, handleClose, caseId, refetch }: CreateBillingNoteModalProps) => {
  const [noteTypes, setNoteTypes] = useState<NoteTypesQuery['fetchAllNoteTypes']['noteTypes']>([])
  const { currentUser } = useContext(AuthContext)

  const methods = useForm<CreateNoteInput>({
    mode: 'all',
    resolver: yupResolver(addCaseNoteValidationSchema),
    defaultValues: {
      description: ''
    }
  })

  const { handleSubmit } = methods

  const [fetchNoteTypes] = useNoteTypesLazyQuery({
    onCompleted(data) {
      if (data?.fetchAllNoteTypes?.noteTypes?.length) {
        const {
          fetchAllNoteTypes: { noteTypes }
        } = data
        setNoteTypes(noteTypes)
      }
    }
  })

  const [createNoteMutation, { loading: createNoteLoading }] = useCreateNoteMutation({
    onError(error) {
      if (error.message === CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE) Alert.error(CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE)
      else Alert.error(SOMETHING_WENT_WRONG)
    },

    onCompleted(data) {
      if (data) {
        const { createNote } = data

        if (createNote) {
          const { response } = createNote
          const { status } = response || {}

          if (status === 200) {
            Alert.success(Note_CREATED_SUCCESS)
            handleClose()
            refetch()
          }
        }
      }
    }
  })

  const onSubmit: SubmitHandler<CreateNoteInput> = async (data: CreateNoteInput) => {
    const { description } = data || {}

    const billingNoteType = noteTypes?.find(note => note?.noteType === 'billing')
    if (!billingNoteType?.id) {
      Alert.error('Unable to create note, please try again later')
      return
    }

    await createNoteMutation({
      variables: {
        createNoteInput: {
          description,
          noteTypeId: billingNoteType.id,
          caseId,
          userId: currentUser?.id
        }
      }
    })
  }

  useEffect(() => {
    fetchNoteTypes()
  }, [fetchNoteTypes])

  return (
    <CustomModal title="Create Note" isOpen={isOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CustomController controllerLabel="Note" controllerName="description" isMultiLine rowsLength={5} />

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
              {CANCEL}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={createNoteLoading}
              aria-label="create note"
              endIcon={createNoteLoading && <CircularProgress size={20} color="inherit" />}
            >
              Create
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

export default CreateBillingNoteModal
